<script setup lang="ts">
import { ref } from "vue";

import type { iconName } from "@/Modules/App/Models/Styles/Icons";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import { isValidString } from "@/Utils/String";

export interface DropDownOption {
  id?: string | number | boolean | unknown;
  label: string;
  extraValue?: string | number | boolean | unknown; //Optional prop for additional information
  icon?: iconName;
  class?: string;
}

export interface TcDropDownProps {
  options: DropDownOption[];
  name: string;
}

const props = withDefaults(defineProps<TcDropDownProps>(), {});

const isOpen = ref<boolean>(false);

const emit = defineEmits<{
  (event: "click", option: DropDownOption): void;
}>();

const handleClick = (op: DropDownOption) => {
  emit("click", op);

  isOpen.value = false;
};

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const handleClickOut = () => {
  isOpen.value = false;
};
</script>

<template>
  <div class="dropdown">
    <slot name="opener" :toggle="() => toggle()" />

    <div class="dropdown__options" v-if="isOpen">
      <slot
        v-for="(option, index) in props.options"
        name="option"
        :option="option"
        :index="index"
        :key="`dropdown_${props.name}_${index}`"
        :clickEvent="() => handleClick(option)"
      >
        <div class="dropdown__options__option" @click="handleClick(option)">
          <div class="dropdown__options__option__icon" v-if="isValidString(option.icon)">
            <TcIcon color="content-high" size="sm" :name="option.icon" />
          </div>

          <div class="dropdown__options__option__label">
            {{ option.label }}
          </div>
        </div>
      </slot>
    </div>

    <div v-if="isOpen" class="dropdown__click_cover" @click="handleClickOut" />
  </div>
</template>
<style scoped lang="scss">
.dropdown {
  position: relative;

  &__click_cover {
    background-color: transparent;
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
  }

  &__options {
    @include elevation(l);
    @include colors.bg(background, base);
    @include radius(xl);
    @include sp.padding(false, 0);

    position: absolute;
    top: 60px;
    z-index: 2;
    width: max-content;
    min-width: 360px;
    max-width: 100%;
    overflow: hidden;

    &__option {
      @include border(1, solid, b);
      @include colors.border(content, low, true);
      @include sp.padding(y, 4);
      @include sp.padding(l, 4);

      display: flex;
      align-items: center;
      cursor: pointer;

      &:hover {
        @include colors.bg(content, low);
      }

      &__icon {
        @include sp.margin(r, 2);
      }

      &__label {
        @include colors.color(content, high);
        @include sp.margin(r, 4);
        @include text.general(low, l);
      }

      &:last-child {
        border: none;
      }
    }
  }
}
</style>
